import { ArrowRight, Sparkles } from 'lucide-react';
import styled from 'styled-components';
import { trackClick } from '../../services/adService';

const ArticleCard = styled.article`
  position: relative;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const ArticleImage = styled.div`
  width: 100%;
  height: 200px;
  background: ${props => props.theme.colors.muted};
  position: relative;
  overflow: hidden;
  
  @media (min-width: 768px) {
    height: 240px;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ArticleContent = styled.div`
  padding: 1.25rem;
  
  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const SponsoredLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const SparklesIcon = styled(Sparkles)`
  width: 1rem;
  height: 1rem;
  color: ${props => props.theme.colors.category.amber};
`;

const SponsoredText = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.mutedForeground};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ArticleTitle = styled.h3`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.125rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.foreground};
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 0.625rem;
  }
`;

const ArticleDescription = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.mutedForeground};
  line-height: 1.75;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 0.9375rem;
    margin-bottom: 1.25rem;
  }
`;

const ReadMoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme.colors.foreground};
  text-decoration: none;
  transition: gap 0.3s;
  
  &:hover {
    gap: 0.75rem;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const SponsoredArticleAd = ({ ad, onClick }) => {
  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) {
      onClick();
    } else {
      trackClick(ad._id);
      window.open(ad.clickUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const imageUrl = ad.imageUrl || '';
  const title = ad.title || '';
  const description = ad.description || '';
  const ctaText = ad.ctaText || 'Read More';

  if (!title && !description) {
    return null;
  }

  return (
    <ArticleCard data-ad-id={ad._id} onClick={handleClick}>
      {imageUrl && (
        <ArticleImage>
          <img src={imageUrl} alt={title || 'Sponsored Article'} />
        </ArticleImage>
      )}
      <ArticleContent>
        <SponsoredLabel>
          <SparklesIcon />
          <SponsoredText>Sponsored</SponsoredText>
        </SponsoredLabel>
        
        {title && <ArticleTitle>{title}</ArticleTitle>}
        {description && <ArticleDescription>{description}</ArticleDescription>}
        
        <ReadMoreLink
          href={ad.clickUrl}
          onClick={handleClick}
          target="_blank"
          rel="noopener noreferrer"
        >
          {ctaText}
          <ArrowRight />
        </ReadMoreLink>
      </ArticleContent>
    </ArticleCard>
  );
};

export default SponsoredArticleAd;



